"use client";

import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Link from "next/link";

const AGENTS = [
  {
    key: "zayn",
    route: "/ai-agents/zayn",
    logo: "https://getgeek-website-assets.b-cdn.net/ZaynCircle.webp",
    bg: "https://getgeek-website-assets.b-cdn.net/HP_BG_New_1.webp",
    tagColor: "#9048FB",
  },
  {
    key: "sage",
    route: "/ai-agents/sage",
    logo: "https://getgeek-website-assets.b-cdn.net/SageCircle.webp",
    bg: "https://getgeek-website-assets.b-cdn.net/HP_BG_New_2.webp",
    tagColor: "#F57A75",
  },
  {
    key: "compass",
    route: "/ai-agents/compass",
    logo: "https://getgeek-website-assets.b-cdn.net/CompassCircle.webp",
    bg: "https://getgeek-website-assets.b-cdn.net/HP_BG_New_3.webp",
    tagColor: "#FFDCAD",
  },
  {
    key: "scout",
    route: "/ai-agents/scout",
    logo: "https://getgeek-website-assets.b-cdn.net/ScoutCircle.webp",
    bg: "https://getgeek-website-assets.b-cdn.net/HP_BG_New_4.webp",
    tagColor: "#D870EE",
  },
  {
    key: "atlas",
    route: "/ai-agents/atlas",
    logo: "https://getgeek-website-assets.b-cdn.net/AtlasCircle.webp",
    bg: "https://getgeek-website-assets.b-cdn.net/HP_BG_New_5.webp",
    tagColor: "#A1E8B5",
  },
  {
    key: "omni",
    route: "/ai-agents/omni",
    logo: "https://getgeek-website-assets.b-cdn.net/GokuCircle.webp",
    bg: "https://getgeek-website-assets.b-cdn.net/HP_BG_New_6.webp",
    tagColor: "#FBBF24",
  },
];

export default function HiringProcess() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  return (
    <Box
      dir={isArabic ? "rtl" : "ltr"}
      sx={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        py: { xs: 6, md: 10 },
        px: { xs: 2, sm: 4, md: 8 },
      }}
    >
      {/* Background */}
      <Box
        component="img"
        src="https://getgeek-website-assets.b-cdn.net/HiringProcessBG.webp"
        alt=""
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 1200, mx: "auto" }}>
        {/* Badge */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: "20px",
              px: 2,
              py: 0.9,
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontSize: { xs: "10px", md: "12px" },
                letterSpacing: "1px",
                color: "rgba(255,255,255,0.8)",
                textTransform: "uppercase",
              }}
            >
              {t("hiringProcess.badge")}
            </Typography>
          </Box>
        </Box>

        {/* Heading */}
        <Typography
          sx={{
            textAlign: "center",
            fontFamily: "var(--font-outfit)",
            fontWeight: 600,
            fontSize: { xs: "24px", sm: "32px", md: "45px" },
            lineHeight: 1.3,
            color: "#fff",
            mb: { xs: 2, md: 3 },
          }}
        >
          {t("hiringProcess.headingPart1")}{" "}
          <Box
            component="span"
            sx={{
              background: "linear-gradient(90deg, #BC8EFF 0%, #BC8EFF 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {t("hiringProcess.headingHighlight")}
          </Box>
          <br />
          {t("hiringProcess.headingPart2")}
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            textAlign: "center",
            fontFamily: "var(--font-outfit)",
            fontWeight: 400,
            fontSize: { xs: "13px", sm: "14px", md: "20px" },
            color: "rgba(255,255,255,0.65)",
            maxWidth: 910,
            mx: "auto",
            mb: { xs: 4, md: 6 },
            lineHeight: 1.8,
          }}
        >
          {t("hiringProcess.subtitle")}
        </Typography>

        {/* Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, 1fr)",
            },
            gap: { xs: 2, md: 3 },
          }}
        >
          {AGENTS.map((agent) => (
            <Box
              key={agent.key}
              component={Link}
              href={agent.route}
              sx={{
                position: "relative",
                display: "block",
                borderRadius: "20px",
                overflow: "hidden",
                minHeight: { xs: 170, md: 190 },
                border: "1px solid #586874",
                textDecoration: "none",
                color: "inherit",
                cursor: "pointer",
                transition: "transform 0.25s ease, border-color 0.25s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  borderColor: "#BC8EFF",
                },
              }}
            >
              {/* Card background */}
              <Box
                component="img"
                src={agent.bg}
                alt=""
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  zIndex: 0,
                }}
              />

              <Stack
                sx={{
                  position: "relative",
                  zIndex: 1,
                  p: { xs: 2.5, md: 3 },
                  height: "100%",
                }}
                spacing={1.5}
              >
                {/* Tag */}
                <Box
                  sx={{
                    alignSelf: "flex-start",
                    bgcolor: `${agent.tagColor}26`,
                    border: `1px solid ${agent.tagColor}4D`,
                    borderRadius: "20px",
                    px: 1.5,
                    py: 0.4,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "9px", md: "10px" },
                      letterSpacing: "0.8px",
                      textTransform: "uppercase",
                      fontWeight: 600,
                      color: agent.tagColor,
                    }}
                  >
                    {t(`hiringProcess.agents.${agent.key}.tag`)}
                  </Typography>
                </Box>

                {/* Logo + Name */}
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ gap: 1.2 }}
                >
                  <Box
                    component="img"
                    src={agent.logo}
                    alt={agent.key}
                    sx={{
                      width: { xs: 28, md: 32 },
                      height: { xs: 28, md: 32 },
                      borderRadius: "50%",
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontWeight: 700,
                      fontSize: { xs: "16px", md: "18px" },
                      color: "#fff",
                      textTransform: "capitalize",
                    }}
                  >
                    {t(`hiringProcess.agents.${agent.key}.name`)}
                  </Typography>
                </Stack>

                {/* Description */}
                <Typography
                  sx={{
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 400,
                    fontSize: { xs: "12.5px", md: "13.5px" },
                    color: "rgba(255,255,255,0.75)",
                    lineHeight: 1.6,
                  }}
                >
                  {t(`hiringProcess.agents.${agent.key}.description`)}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 4, md: 5 } }}>
          <Box
            component={Link}
            href="/ai-agents"
            sx={{
              fontFamily: "var(--font-outfit)",
              fontWeight: 600,
              fontSize: { xs: "13px", md: "15px" },
              color: "#fff",
              bgcolor: "transparent",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "30px",
              px: { xs: 3, md: 4 },
              py: { xs: 1.2, md: 1.5 },
              cursor: "pointer",
              textDecoration: "none",
              display: "inline-block",
              transition: "all 0.25s ease",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.08)",
                borderColor: "#BC8EFF",
              },
              mt: "1%",
            }}
          >
            {t("hiringProcess.exploreButton")}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}